import { useCallback, useEffect, useMemo, useState } from 'react';

import { cn } from '@/lib/utils';
import {
  type Direction,
  type GameState,
  type PuzzleData,
  Cell,
  activatedCount,
  createInitialState,
  directionArrow,
  step,
  totalWalkable,
  useCane,
} from '@/lib/puzzle';

// ─── Tile rendering ─────────────────────────────────────────────────────────

interface TileProps {
  cell: number;
  activated: boolean;
  /** True if this tile is the yellow marker position. */
  isYellow: boolean;
  /** True if the character (white dot) is on this tile. */
  isCharacter: boolean;
  isSomariaBlock: boolean;
  facing: Direction;
  tileSize: number;
  isStart: boolean;
  won: boolean;
}

function Tile({ cell, activated, isYellow, isCharacter, isSomariaBlock, facing, tileSize, isStart, won }: TileProps) {
  const sizeStyle = { width: tileSize, height: tileSize };

  // Wall tile — dark, non-interactive.
  if (cell === Cell.Wall) {
    return (
      <div
        className="rounded-md bg-gray-800 dark:bg-gray-950 border border-gray-900 dark:border-black shadow-inner"
        style={sizeStyle}
      />
    );
  }

  // Somaria block — purple obstacle.
  if (isSomariaBlock) {
    return (
      <div
        className="rounded-md bg-gradient-to-br from-purple-500 to-purple-700 border border-purple-300 shadow-lg flex items-center justify-center"
        style={sizeStyle}
      >
        <div
          className="rounded-full bg-purple-300/40 border-2 border-purple-200"
          style={{ width: tileSize * 0.5, height: tileSize * 0.5 }}
        />
      </div>
    );
  }

  // ── Character tile (has direction arrow + white dot) ────────────────────
  // The arrow always goes on the character, wherever it is.
  if (isCharacter) {
    const isOnYellow = isYellow;
    const isPassive = cell === Cell.Passive;

    // Background depends on what tile the character is on:
    //  - Yellow tile → yellow background
    //  - Passive → grey background
    //  - Activated → red background
    //  - Unactivated blue → blue background (character walked here without
    //    activating because it wasn't on the yellow tile)
    let bg: string;
    let arrowColor: string;
    let dotBorder: string;

    if (isOnYellow) {
      bg = won
        ? 'bg-gradient-to-br from-yellow-300 to-amber-500 border-yellow-200 shadow-lg shadow-yellow-400/50'
        : 'bg-gradient-to-br from-yellow-400 to-yellow-500 border-yellow-300 shadow-lg shadow-yellow-400/40';
      arrowColor = 'text-yellow-900';
      dotBorder = 'rgb(202 138 4)';
    } else if (isPassive) {
      bg = 'bg-gradient-to-br from-gray-300 to-gray-400 border-gray-200 dark:from-gray-600 dark:to-gray-700 dark:border-gray-500 shadow-md shadow-gray-400/20';
      arrowColor = 'text-gray-700 dark:text-gray-200';
      dotBorder = 'rgb(107 114 128)';
    } else if (activated) {
      bg = 'bg-gradient-to-br from-red-400 to-red-500 border-red-300 dark:from-red-500 dark:to-red-600 dark:border-red-400 shadow-md shadow-red-400/20';
      arrowColor = 'text-red-900 dark:text-red-100';
      dotBorder = 'rgb(220 38 38)';
    } else {
      // Unactivated blue tile — character walked here without activating.
      bg = 'bg-gradient-to-br from-sky-300 to-sky-400 border-sky-200 dark:from-sky-600 dark:to-sky-700 dark:border-sky-500 shadow-md shadow-sky-400/20';
      arrowColor = 'text-sky-800 dark:text-sky-100';
      dotBorder = 'rgb(2 132 199)';
    }

    return (
      <div
        className={cn(
          'rounded-md border-2 flex items-center justify-center relative transition-all duration-200',
          bg,
          isStart && !isOnYellow && 'ring-2 ring-offset-1',
        )}
        style={sizeStyle}
      >
        <span
          className={cn('font-bold select-none', arrowColor)}
          style={{ fontSize: tileSize * 0.45, lineHeight: 1 }}
        >
          {directionArrow(facing)}
        </span>
        <div
          className="absolute rounded-full bg-white border-2 shadow-md"
          style={{
            width: tileSize * 0.28,
            height: tileSize * 0.28,
            top: tileSize * 0.1,
            right: tileSize * 0.1,
            borderColor: dotBorder,
          }}
        />
        {isStart && isOnYellow && !won && (
          <div
            className="absolute rounded-full border-2 border-yellow-700/60"
            style={{ width: tileSize * 0.7, height: tileSize * 0.7 }}
          />
        )}
      </div>
    );
  }

  // ── Yellow tile without character ────────────────────────────────────────
  // Just a yellow marker — no arrow, no dot.
  if (isYellow) {
    return (
      <div
        className={cn(
          'rounded-md border-2 flex items-center justify-center relative transition-all duration-200',
          won
            ? 'bg-gradient-to-br from-yellow-300 to-amber-500 border-yellow-200 shadow-lg shadow-yellow-400/50'
            : 'bg-gradient-to-br from-yellow-400 to-yellow-500 border-yellow-300 shadow-lg shadow-yellow-400/40',
          isStart && !won && 'ring-2 ring-yellow-200 ring-offset-1',
        )}
        style={sizeStyle}
      >
        {/* Small dot to mark this as the yellow/anchor tile */}
        <div
          className="rounded-full bg-yellow-200/60 border border-yellow-400"
          style={{ width: tileSize * 0.25, height: tileSize * 0.25 }}
        />
      </div>
    );
  }

  // ── Passive tile (no character, no yellow) ───────────────────────────────
  if (cell === Cell.Passive) {
    return (
      <div
        className={cn(
          'rounded-md border transition-colors duration-150',
          'bg-gradient-to-br from-gray-300 to-gray-400 border-gray-200 dark:from-gray-600 dark:to-gray-700 dark:border-gray-500',
          isStart && 'ring-2 ring-gray-300 ring-offset-1',
        )}
        style={sizeStyle}
      />
    );
  }

  // ── Activated tile — red ─────────────────────────────────────────────────
  if (activated) {
    return (
      <div
        className={cn(
          'rounded-md border transition-colors duration-150',
          'bg-gradient-to-br from-red-400 to-red-500 border-red-300 dark:from-red-500 dark:to-red-600 dark:border-red-400',
          isStart && 'ring-2 ring-red-200 ring-offset-1',
        )}
        style={sizeStyle}
      />
    );
  }

  // ── Unactivated walkable tile — light blue ───────────────────────────────
  return (
    <div
      className={cn(
        'rounded-md border transition-colors duration-150',
        'bg-gradient-to-br from-sky-300 to-sky-400 border-sky-200 dark:from-sky-600 dark:to-sky-700 dark:border-sky-500',
        isStart && 'ring-2 ring-sky-200 ring-offset-1',
      )}
      style={sizeStyle}
    />
  );
}

// ─── D-pad control ──────────────────────────────────────────────────────────

interface DPadProps {
  onDirection: (dir: Direction) => void;
  onCane: () => void;
  onUndo: () => void;
  onReset: () => void;
  somaria: boolean;
  canUndo: boolean;
  won: boolean;
}

function DPad({ onDirection, onCane, onUndo, onReset, somaria, canUndo, won }: DPadProps) {
  const btnClass = 'h-12 w-12 rounded-lg flex items-center justify-center text-xl font-bold transition-all active:scale-90 select-none';

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="grid grid-cols-3 gap-1.5">
        <div />
        <button
          className={cn(btnClass, 'bg-sky-500 text-white hover:bg-sky-600 shadow-md disabled:opacity-40')}
          onClick={() => onDirection('up')}
          disabled={won}
          aria-label="Step up"
        >
          {'\u2191'}
        </button>
        <div />

        <button
          className={cn(btnClass, 'bg-sky-500 text-white hover:bg-sky-600 shadow-md disabled:opacity-40')}
          onClick={() => onDirection('left')}
          disabled={won}
          aria-label="Step left"
        >
          {'\u2190'}
        </button>
        <button
          className={cn(btnClass, 'bg-slate-600 text-white hover:bg-slate-700 shadow-md disabled:opacity-40')}
          onClick={onReset}
          aria-label="Reset puzzle"
        >
          {'\u21BB'}
        </button>
        <button
          className={cn(btnClass, 'bg-sky-500 text-white hover:bg-sky-600 shadow-md disabled:opacity-40')}
          onClick={() => onDirection('right')}
          disabled={won}
          aria-label="Step right"
        >
          {'\u2192'}
        </button>

        <div />
        <button
          className={cn(btnClass, 'bg-sky-500 text-white hover:bg-sky-600 shadow-md disabled:opacity-40')}
          onClick={() => onDirection('down')}
          disabled={won}
          aria-label="Step down"
        >
          {'\u2193'}
        </button>
        <div />
      </div>

      <div className="flex gap-2">
        {somaria && (
          <button
            className={cn(
              'h-10 px-4 rounded-lg text-sm font-semibold transition-all active:scale-95 shadow-md disabled:opacity-40',
              'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700',
            )}
            onClick={onCane}
            disabled={won}
          >
            {'Cane of Somaria'}
          </button>
        )}
        <button
          className={cn(
            'h-10 px-4 rounded-lg text-sm font-semibold transition-all active:scale-95 shadow-md disabled:opacity-40',
            'bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600',
          )}
          onClick={onUndo}
          disabled={!canUndo || won}
        >
          {'Undo'}
        </button>
      </div>
    </div>
  );
}

// ─── Main game component ────────────────────────────────────────────────────

export interface TilePuzzleGameProps {
  puzzle: PuzzleData;
  onSolve?: () => void;
  className?: string;
}

export function TilePuzzleGame({ puzzle, onSolve, className }: TilePuzzleGameProps) {
  const [state, setState] = useState<GameState>(() => createInitialState(puzzle));
  const [history, setHistory] = useState<GameState[]>([]);

  // Reset when puzzle changes.
  useEffect(() => {
    setState(createInitialState(puzzle));
    setHistory([]);
  }, [puzzle]);

  // Win callback.
  useEffect(() => {
    if (state.won && onSolve) {
      onSolve();
    }
  }, [state.won, onSolve]);

  // Calculate tile size based on grid dimensions.
  const tileSize = useMemo(() => {
    const maxDim = Math.max(puzzle.width, puzzle.height);
    if (maxDim <= 5) return 64;
    if (maxDim <= 7) return 56;
    if (maxDim <= 10) return 48;
    if (maxDim <= 15) return 40;
    return 32;
  }, [puzzle.width, puzzle.height]);

  const total = useMemo(
    () => totalWalkable(puzzle, state.somariaBlock),
    [puzzle, state.somariaBlock],
  );
  const done = useMemo(
    () => activatedCount(state, puzzle, state.somariaBlock),
    [state, puzzle],
  );

  // ── Actions ──────────────────────────────────────────────────────────────

  const handleDirection = useCallback((dir: Direction) => {
    setState((prev) => {
      if (prev.won) return prev;

      // If pressing a different direction than the character currently faces,
      // just turn to look that way — don't move yet. The next press of the
      // same direction will actually step.
      if (dir !== prev.facing) {
        return { ...prev, facing: dir };
      }

      // Already facing this direction — step one tile.
      const newState = step(prev, puzzle, dir);
      // Only record history when the character actually moved position.
      if (newState.posX !== prev.posX || newState.posY !== prev.posY) {
        setHistory((h) => [...h, prev]);
      }
      return newState;
    });
  }, [puzzle]);

  const handleCane = useCallback(() => {
    setState((prev) => {
      if (prev.won) return prev;
      const newState = useCane(prev, puzzle);
      if (newState !== prev) {
        setHistory((h) => [...h, prev]);
      }
      return newState;
    });
  }, [puzzle]);

  const handleUndo = useCallback(() => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setState(prev);
      return h.slice(0, -1);
    });
  }, []);

  const handleReset = useCallback(() => {
    setState(createInitialState(puzzle));
    setHistory([]);
  }, [puzzle]);

  // ── Keyboard ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't capture if focus is in an input/textarea.
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          e.preventDefault();
          handleDirection('up');
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          e.preventDefault();
          handleDirection('down');
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          e.preventDefault();
          handleDirection('left');
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          e.preventDefault();
          handleDirection('right');
          break;
        case ' ':
        case 'Enter':
          e.preventDefault();
          handleCane();
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          handleReset();
          break;
        case 'z':
        case 'Z':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleUndo();
          }
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleDirection, handleCane, handleReset, handleUndo]);

  // ── Render ────────────────────────────────────────────────────────────────

  const gridWidth = puzzle.width * tileSize + (puzzle.width - 1) * 4;
  const gridHeight = puzzle.height * tileSize + (puzzle.height - 1) * 4;

  return (
    <div className={cn('flex flex-col items-center gap-6', className)}>
      {/* Progress bar */}
      <div className="w-full max-w-md flex items-center gap-3">
        <div className="flex-1 h-3 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-sky-400 to-red-500 transition-all duration-300"
            style={{ width: `${total > 0 ? (done / total) * 100 : 0}%` }}
          />
        </div>
        <span className="text-sm font-semibold text-muted-foreground tabular-nums whitespace-nowrap">
          {done}/{total}
        </span>
      </div>

      {/* Game board */}
      <div
        className="relative rounded-2xl bg-slate-100 dark:bg-slate-800 p-3 shadow-xl"
        style={{ width: gridWidth + 24, height: gridHeight + 24 }}
      >
        <div
          className="grid gap-1"
          style={{
            gridTemplateColumns: `repeat(${puzzle.width}, ${tileSize}px)`,
            gridTemplateRows: `repeat(${puzzle.height}, ${tileSize}px)`,
          }}
        >
          {puzzle.cells.map((cell, i) => {
            const x = i % puzzle.width;
            const y = Math.floor(i / puzzle.width);
            const isCharacter = x === state.posX && y === state.posY;
            const isYellow = x === state.yellowX && y === state.yellowY;
            const isStart = x === puzzle.startX && y === puzzle.startY;
            return (
              <Tile
                key={i}
                cell={cell}
                activated={state.activated[i]}
                isYellow={isYellow}
                isCharacter={isCharacter}
                isSomariaBlock={state.somariaBlock === i}
                facing={state.facing}
                tileSize={tileSize}
                isStart={isStart}
                won={state.won}
              />
            );
          })}
        </div>

        {/* Win overlay */}
        {state.won && (
          <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/40 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="text-center px-8 py-6 rounded-2xl bg-white dark:bg-slate-900 shadow-2xl">
              <div className="text-4xl mb-2">{'\u2705'}</div>
              <h3 className="text-xl font-bold text-foreground">Puzzle Solved!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {state.moves} {state.moves === 1 ? 'move' : 'moves'}
                {puzzle.somaria && state.somariaBlock !== null ? ' with Somaria' : ''}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <DPad
        onDirection={handleDirection}
        onCane={handleCane}
        onUndo={handleUndo}
        onReset={handleReset}
        somaria={puzzle.somaria}
        canUndo={history.length > 0}
        won={state.won}
      />

      {/* Keyboard hint */}
      <p className="text-xs text-muted-foreground text-center max-w-sm">
        Use arrow keys or WASD to step. Press a new direction once to turn, again to move.
        Step from the yellow tile onto a blue tile to activate it.{' '}
        {puzzle.somaria && 'Press Space for the Cane of Somaria. Walk into a block to push it. '}
        Press R to reset, Ctrl+Z to undo.
      </p>
    </div>
  );
}
